import React, { useState } from 'react';
import { Button, CircularProgress, Stack, Typography } from '@mui/material';
import { DownloadOutlined } from '@mui/icons-material';

import { processFormFieldKind } from './processFormContract';

export interface ProcessAttachmentMeta {
  id: string;
  field_id: string;
  file_name: string;
  content_type: string;
  size_bytes: number;
  document_kind?: string | null;
  status: string;
  created_at?: string;
}

export interface ProcessLocationSnapshot {
  id?: string;
  name?: string;
  address?: string | null;
  street_address?: string | null;
  city?: string | null;
  state?: string | null;
  country?: string | null;
  postal_code?: string | null;
  timezone?: string | null;
}

export function isLocationSnapshot(value: unknown): value is ProcessLocationSnapshot {
  return Boolean(value && typeof value === 'object' && typeof (value as ProcessLocationSnapshot).name === 'string');
}

export function formatLocationAddress(value: ProcessLocationSnapshot): string {
  const explicit = value.address?.trim();
  if (explicit) return explicit;
  return [value.street_address, value.city, value.state, value.postal_code, value.country]
    .filter((part): part is string => typeof part === 'string' && Boolean(part.trim()))
    .join(', ');
}

export interface ProcessReferenceSnapshot {
  id?: string;
  name?: string;
  worker_code?: string | null;
  email?: string | null;
  /** Departments carry only id + name; the field is here so one type covers both. */
  parent_name?: string | null;
}

/**
 * `worker_reference` / `department_reference` are stored as server snapshots
 * (`resolve_authoritative_form_values`), so the raw value is an OBJECT. Every read
 * surface here used to fall through to `String(value)` and print `[object Object]`
 * — the submit side and the display side of one field type, missed together.
 */
export function isReferenceSnapshot(value: unknown): value is ProcessReferenceSnapshot {
  return Boolean(
    value && typeof value === 'object'
    && typeof (value as ProcessReferenceSnapshot).name === 'string',
  );
}

/**
 * The field kinds whose STORED value is not a display string, so a read surface must
 * hand them to this component instead of `String(value)`.
 *
 * One list, four call sites (WorkerRequestDetail, WorkerTasks ×2, RequestList,
 * WorkerSubmitRequest review). Each of those used to spell `kind === 'location' ||
 * kind === 'attachment'` inline, which is why adding the two reference branches
 * INSIDE this component changed nothing anywhere — the component was correct and
 * unreachable (svdy-ui §12.12). Extend this array, never the conditions.
 */
export const PROCESS_RESOURCE_KINDS = [
  'location', 'attachment', 'worker', 'department',
] as const;

export function isProcessResourceKind(kind: string | undefined): boolean {
  return (PROCESS_RESOURCE_KINDS as readonly string[]).includes(kind ?? '');
}

export function formatFileSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.ceil(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(bytes < 10 * 1024 * 1024 ? 1 : 0)} MB`;
}

/** Metadata supplies the download name only; it is never treated as a URL. */
export function safeAttachmentFilename(fileName: string): string {
  const leaf = fileName.split(/[\\/]/).pop()?.trim() || 'attachment';
  const cleaned = Array.from(leaf)
    .filter((character) => {
      const code = character.charCodeAt(0);
      return code > 31 && code !== 127;
    })
    .join('');
  return cleaned.slice(0, 180) || 'attachment';
}

export function attachmentForField(
  attachments: ProcessAttachmentMeta[] | null | undefined,
  fieldId: string,
  value: unknown,
): ProcessAttachmentMeta | undefined {
  return (attachments ?? []).find((attachment) =>
    attachment.field_id === fieldId || (typeof value === 'string' && attachment.id === value),
  );
}

type Translate = (key: string, options?: Record<string, unknown>) => string;

interface Props {
  fieldId: string;
  fieldType: string | undefined;
  value: unknown;
  attachments?: ProcessAttachmentMeta[] | null;
  onDownload?: (attachment: ProcessAttachmentMeta) => Promise<void>;
  t: Translate;
}

const ProcessFormResourceValue: React.FC<Props> = ({
  fieldId, fieldType, value, attachments, onDownload, t,
}) => {
  const [downloading, setDownloading] = useState(false);
  if (fieldType === 'organization_location') {
    if (!isLocationSnapshot(value)) {
      return <Typography variant="body2" color="text.secondary">{t('worker:processResources.locationUnavailable')}</Typography>;
    }
    const address = formatLocationAddress(value);
    return (
      <Stack spacing={0.25}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>{value.name}</Typography>
        {address && <Typography variant="caption" color="text.secondary">{address}</Typography>}
        {value.timezone && <Typography variant="caption" color="text.secondary">{t('worker:processResources.timezone', { timezone: value.timezone })}</Typography>}
      </Stack>
    );
  }
  const referenceKind = processFormFieldKind(fieldType);
  if (referenceKind === 'worker' || referenceKind === 'department') {
    if (!isReferenceSnapshot(value)) {
      // A bare id string reaches here only for a row written before the resolver
      // ran, or a legacy free-text value. Show it rather than an empty cell, but
      // never `[object Object]`.
      const raw = typeof value === 'string' ? value.trim() : '';
      return (
        <Typography variant="body2" color={raw ? undefined : 'text.secondary'}>
          {raw || t(referenceKind === 'worker'
            ? 'worker:processResources.workerUnavailable'
            : 'worker:processResources.departmentUnavailable')}
        </Typography>
      );
    }
    const meta = referenceKind === 'worker'
      ? [value.worker_code, value.email].filter(Boolean).join(' · ')
      : value.parent_name ?? '';
    return (
      <Stack spacing={0.25}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>{value.name}</Typography>
        {meta && <Typography variant="caption" color="text.secondary">{meta}</Typography>}
      </Stack>
    );
  }
  if (fieldType === 'file_attachment') {    const attachment = attachmentForField(attachments, fieldId, value);
    if (!attachment) {
      return <Typography variant="body2" color="text.secondary">{t('worker:processResources.attachmentUnavailable')}</Typography>;
    }
    const download = async () => {
      if (!onDownload || downloading) return;
      setDownloading(true);
      try {
        await onDownload(attachment);
      } finally {
        setDownloading(false);
      }
    };
    return (
      <Stack spacing={0.25} alignItems="flex-start">
        <Button
          size="small"
          variant="text"
          startIcon={downloading ? <CircularProgress size={14} /> : <DownloadOutlined fontSize="small" />}
          onClick={download}
          disabled={!onDownload || downloading}
          sx={{ p: 0, minWidth: 0, textTransform: 'none', justifyContent: 'flex-start' }}
        >
          {safeAttachmentFilename(attachment.file_name)}
        </Button>
        <Typography variant="caption" color="text.secondary">
          {attachment.document_kind === 'invoice'
            ? t('worker:processResources.invoiceFile')
            : t('worker:processResources.generalFile')}
          {' · '}{formatFileSize(attachment.size_bytes)}
        </Typography>
      </Stack>
    );
  }
  return <Typography variant="body2">{value == null || value === '' ? '—' : String(value)}</Typography>;
};

export default ProcessFormResourceValue;

/**
 * A scalar submitted value, as prose.
 *
 * Shared 2026-09-08. This was file-local in `RequestList.tsx`, and the admin instance page needed the
 * same formatting — copying it would have made two definitions of "how a submitted value reads", which is
 * the drift this module exists to hold in one place (svdy-ui §20).
 *
 * An object reaching here means the schema could not name the field's type, so it SAYS so rather than
 * pasting a brace: the resource renderer above handles every kind that legitimately stores an object, and
 * `[object Object]` on an approver's screen is the defect this whole area was reported for.
 */
export function formatPayloadValue(
  value: unknown,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string {
  if (value == null || value === '') return '\u2014';
  if (Array.isArray(value)) {
    // The engine's only list-valued field type. Joined rather than JSON-dumped.
    return value.length ? value.map((item) => String(item)).join(', ') : '\u2014';
  }
  if (typeof value === 'object') {
    return t('processes:requestList.unsupportedValue');
  }
  return String(value);
}
